import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { create } from '@bufbuild/protobuf';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { sandboxApi } from '@/api/sandbox.api';
import { formatTimestamp } from '@/utils/utils';
import {
  TraceRecordStatus,
  ListSandboxTraceRecordsRequestSchema,
} from '@/proto/agentplatform/httpv2/sandbox_pb';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import { TraceIcon, EmptyBoxIcon, ErrorCircleIcon } from './shared';

const TIME_RANGES = [
  { label: '过去 3 小时', hours: 3 },
  { label: '过去 6 小时', hours: 6 },
  { label: '过去 12 小时', hours: 12 },
  { label: '过去 1 天', hours: 24 },
  { label: '过去 7 天', hours: 168 },
  { label: '过去 30 天', hours: 720 },
];

function rangeToTimestamps(hours: number | null): { from?: string; to?: string } {
  if (hours === null) return {};
  const now = new Date();
  const from = new Date(now.getTime() - hours * 60 * 60 * 1000);
  return { from: from.toISOString(), to: now.toISOString() };
}

const statusConfig: Record<
  number,
  { label: string; dot: string; badge: 'default' | 'secondary' | 'outline' | 'destructive' }
> = {
  [TraceRecordStatus.RUNNING]: {
    label: '运行中',
    dot: 'bg-blue-500 animate-pulse',
    badge: 'default',
  },
  [TraceRecordStatus.FINISHED]: { label: '已完成', dot: 'bg-emerald-500', badge: 'secondary' },
};

function getStatus(record: { status?: number }) {
  return (
    statusConfig[record.status ?? -1] ?? {
      label: '未知',
      dot: 'bg-gray-400',
      badge: 'outline' as const,
    }
  );
}

export default function SandboxTraces() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [timeRange, setTimeRange] = useState<number | null>(null);
  const [selectedInstanceId, setSelectedInstanceId] = useState('');

  const timestamps = useMemo(() => rangeToTimestamps(timeRange), [timeRange]);

  const {
    data: traceRecordsData,
    isLoading: traceRecordsLoading,
    error: traceRecordsError,
    refetch: traceRecordsRefetch,
  } = useQuery({
    queryKey: [
      'sandbox-trace-records',
      page,
      pageSize,
      timestamps.from,
      timestamps.to,
      selectedInstanceId,
    ],
    queryFn: () =>
      sandboxApi.listSandboxTraceRecords(
        create(ListSandboxTraceRecordsRequestSchema, {
          page,
          pageSize,
          fromTimestamp: timestamps.from ?? '',
          toTimestamp: timestamps.to ?? '',
          tags: selectedInstanceId ? [`user_tag_sandbox_id=${selectedInstanceId}`] : [],
        })
      ),
  });

  const { data: instancesData } = useQuery({
    queryKey: ['sandbox-instances-for-filter'],
    queryFn: () => sandboxApi.listSandboxes(create(ListRequestSchema, { page: 1, pageSize: 200 })),
  });

  const instances = (instancesData?.data || []).filter(Boolean);
  const traceRecords = traceRecordsData?.data || [];
  const totalPages = Math.max(1, Math.ceil((traceRecordsData?.total || 0) / pageSize));

  return (
    <>
      <div className="bg-background rounded-xl border shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b space-y-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <TraceIcon />
              <span className="text-sm font-medium text-foreground">沙箱运行记录</span>
              {!traceRecordsLoading && traceRecords.length > 0 && (
                <span className="text-xs text-muted-foreground ml-1">
                  {traceRecordsData?.total ?? 0} 条记录
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground shrink-0">时间范围</span>
              <select
                value={timeRange ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setTimeRange(v ? Number(v) : null);
                  setPage(1);
                }}
                className="h-8 rounded-md border border-input bg-background px-2.5 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
              >
                <option value="">全部</option>
                {TIME_RANGES.map((r) => (
                  <option key={r.hours} value={r.hours}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground shrink-0">实例</span>
              <select
                value={selectedInstanceId}
                onChange={(e) => {
                  setSelectedInstanceId(e.target.value);
                  setPage(1);
                }}
                className="h-8 rounded-md border border-input bg-background px-2.5 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer max-w-[200px]"
              >
                <option value="">全部</option>
                {instances.map((inst) => (
                  <option key={inst.id} value={inst.id}>
                    {inst.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {traceRecordsLoading ? (
          <div className="p-5 space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-lg border p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-5 w-24" />
                  <Skeleton className="h-4 w-28" />
                </div>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-3 w-32" />
                  <Skeleton className="h-3 w-4" />
                  <Skeleton className="h-3 w-32" />
                </div>
                <Skeleton className="h-2 w-full" />
              </div>
            ))}
          </div>
        ) : traceRecordsError ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
              <span className="text-destructive">
                <ErrorCircleIcon />
              </span>
            </div>
            <p className="font-semibold text-foreground mb-1">加载失败</p>
            <p className="text-sm text-muted-foreground mb-5">请检查网络连接后重试</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => traceRecordsRefetch()}
              className="gap-1.5"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              重试
            </Button>
          </div>
        ) : traceRecords.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <EmptyBoxIcon />
            <p className="font-semibold text-foreground mt-4 mb-1">暂无运行记录</p>
            <p className="text-sm text-muted-foreground mb-5">暂无沙箱操作运行记录</p>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {traceRecords.map((record, idx) => {
              const status = getStatus(record);
              const hasStart = !!record.startTime;

              const durationMs = record.duration ?? 0;
              const durationLabel = durationMs > 0 ? `${(durationMs / 1000).toFixed(1)}s` : '-';
              const endTimeNano =
                hasStart && durationMs > 0
                  ? BigInt(record.startTime) + BigInt(Math.round(durationMs * 1e6))
                  : undefined;

              return (
                <div
                  key={idx}
                  className="group rounded-lg border bg-card hover:border-primary/30 transition-colors"
                >
                  <div className="p-4 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${status.dot}`} />
                        <Badge variant={status.badge} className="text-[11px] px-2 py-0">
                          {status.label}
                        </Badge>
                      </div>
                      <span className="text-[11px] text-muted-foreground font-mono">
                        {record.sandboxId || '-'}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground shrink-0">开始</span>
                      <span className="font-mono text-foreground/80">
                        {hasStart ? formatTimestamp(record.startTime) : '-'}
                      </span>
                      <span className="text-muted-foreground/40 mx-1">→</span>
                      <span className="text-muted-foreground shrink-0">结束</span>
                      <span className="font-mono text-foreground/80">
                        {endTimeNano !== undefined ? formatTimestamp(endTimeNano) : '-'}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                      <span>耗时</span>
                      <span className="font-mono font-medium text-foreground/70">
                        {durationLabel}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                      <span className="shrink-0">Trace ID</span>
                      <span className="font-mono text-foreground/70 truncate">
                        {record.traceId || '-'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!traceRecordsLoading && !traceRecordsError && traceRecords.length > 0 && (
          <div className="flex items-center justify-between px-5 py-4 border-t bg-muted/10 gap-4">
            <p className="text-sm text-muted-foreground">
              共 <span className="font-medium text-foreground">{traceRecordsData?.total || 0}</span>{' '}
              条
            </p>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground">每页</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="h-7 rounded-md border border-input bg-background px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-xs text-muted-foreground">条</span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </Button>
                <div className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-background border text-xs">
                  <span className="font-medium">{page}</span>
                  <span className="text-muted-foreground">/</span>
                  <span className="text-muted-foreground">{totalPages}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
